<template>
  <div>

    <v-row class="mb-12">

      <v-col cols="12" sm="9" md="10" xl="11">
        <h2>Log for {{ locationName }}</h2>
      </v-col>

      <v-col cols="12" sm="3" md="2" xl="1" class="text-right">
        <v-btn
          :to="{ name: 'rateManagement', params: { locationID: props.locationID } }"
          prepend-icon="$back"
        >Back</v-btn>
      </v-col>

    </v-row>

    <audit-log-table
      show-target-type
      :target-types="formState.targetTypes"
      :target-ids="formState.targetId ? [formState.targetId] : null"
      :locationID="props.locationID"
      :headers="headers"
      :transformer="transformer"
    >

      <template v-slot:tools>

        <table-filter>
          <o-select
            :label="$t('titles.target_type')"
            :items="[{value: 'default_rate', title: 'Default Rates'}, {value: 'spot_rate', title: 'Spot Rates'}, {value: 'event_tier', title: 'Event Tier'}, {value: 'venue_location_product', title: 'Venue Location Product'}, {value: 'venue_tier', title: 'Venue Tier'}]"
            v-model="pendingFormState.targetTypes"
            :rules="pendingFormRules.targetTypes"
            multiple
            @blur="() => { if (pendingFormState.targetTypes) { formState.targetTypes = pendingFormState.targetTypes }}"
            clearable
          />
        </table-filter>

        <table-filter v-if="canSeeIds">
          <o-text-field
            :label="$t('titles.target_id')"
            v-model.number="pendingFormState.targetId"
            :rules="pendingFormRules.targetId"
            @blur="() => formState.targetId = pendingFormState.targetId"
            type="number"
            clearable
          />
        </table-filter>

      </template>

      <!--
      <template v-slot:item.channel="{ item }">
        <template v-if="item.channel">
          {{ ChannelRegistry.find(item.channel)?.title ?? item.channel }}
        </template>
      </template>
      -->

      <template v-slot:item.productName="{ item }">
        <template v-if="canEditLocationProducts && item.locationProductID">
          <router-link :to="{name: 'locationProductDetail', params: {locationProductID: item.locationProductID}}">
            {{ item.productName }}
          </router-link>
        </template>
        <template v-else>
          {{ item.productName }}
        </template>
      </template>

      <template v-slot:item.startDate="{ item }">

        <template v-if="item.startDate">
          {{ Temporal.PlainDate.from(item.startDate).toLocaleString() }}
          <template v-if="item.endDate !== item.startDate">
            – {{ Temporal.PlainDate.from(item.endDate).toLocaleString() }}
          </template>
        </template>

      </template>

      <template v-slot:item.channel="{ value }">
        {{ ChannelRegistry.findTitle(value) }}
      </template>

      <template v-slot:item.actionDetails="{ item }">

        <template v-if="item.targetType === 'default_rate'">

          <template v-if="item.actionDetails?.default_cost">
            {{ currencyFormatter.format(item.actionDetails.default_cost) }}
          </template>
          <template v-else>
            ?
          </template>

          <template v-if="item.actionDetails.per_quantity && item.actionDetails.per_unit">
            for {{ item.actionDetails.per_quantity }} {{ item.actionDetails.per_unit }}{{ item.actionDetails.per_quantity > 1 ? 's' : ''}}
          </template>

        </template>

        <template v-if="item.targetType === 'spot_rate'">

          <template v-if="item.actionDetails?.spot_cost">
            {{ currencyFormatter.format(item.actionDetails.spot_cost) }}
          </template>
          <template v-else>
            ?
          </template>

          <template v-if="item.actionDetails.per_quantity && item.actionDetails.per_unit">
            for {{ item.actionDetails.per_quantity }} {{ item.actionDetails.per_unit }}{{ item.actionDetails.per_quantity > 1 ? 's' : ''}}
          </template>

        </template>

        <template v-if="item.targetType === 'venue_location_product'">

          <table class="minitable">
            <thead>
              <tr>
                <th scope="col">{{ $t('titles.field') }}</th>
                <th scope="col">{{ $t('titles.old' ) }}</th>
                <th scope="col">{{ $t('titles.new' ) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{{ $t('titles.default_cost') }}</th>
                <td>
                  <template v-if="item.actionDetails?.previous_state?.default_price">
                    {{ currencyFormatter.format(item.actionDetails.previous_state.default_price) }}
                  </template>
                  <template v-else>
                    ?
                  </template>
                </td>
                <td>
                  <template v-if="item.actionDetails?.default_price">
                    {{ currencyFormatter.format(item.actionDetails.default_price) }}
                  </template>
                  <template v-else>
                    ?
                  </template>
                </td>
              </tr>
              <tr>
                <th scope="row">{{ $t('titles.default_inventory') }}</th>
                <td>{{ item.actionDetails?.previous_state?.default_inventory ?? '?' }}</td>
                <td>{{ item.actionDetails?.default_inventory ?? '?' }}</td>
              </tr>
              <tr>
                <th scope="row">{{ $t('titles.entry_hours_before_event') }}</th>
                <td>
                  {{ item.actionDetails?.previous_state?.entry_hours_before_event ?? '?' }}
                </td>
                <td>
                  {{ item.actionDetails?.entry_hours_before_event ?? '?' }}
                </td>
              </tr>
              <tr>
                <th scope="row">{{ $t('titles.exit_hours_after_event') }}</th>
                <td>
                  {{ item.actionDetails?.previous_state?.entry_hours_before_event ?? '?' }}
                </td>
                <td>
                  {{ item.actionDetails?.entry_hours_before_event ?? '?' }}
                </td>
              </tr>
              <tr>
                <th scope="row">{{ $t('misc.live') }}</th>
                <td>
                  <template v-if="item.actionDetails?.previous_state?.live === true">{{ $t('misc.yes') }}</template>
                  <template v-else-if="item.actionDetails?.previous_state?.live === true">{{ $t('misc.no') }}</template>
                  <template v-else>?</template>
                </td>
                <td>
                  <template v-if="item.actionDetails?.live === true">{{ $t('misc.yes') }}</template>
                  <template v-else-if="item.actionDetails?.live === true">{{ $t('misc.no') }}</template>
                  <template v-else>?</template>
                </td>
              </tr>
            </tbody>
          </table>

        </template>

        <template v-if="item.targetType === 'venue_tier'">
          <template v-if="item.actionDetails?.tier_price">
            {{ currencyFormatter.format(item.actionDetails?.tier_price) }}
          </template>
          <template v-else>
            ?
          </template>
          up to
          <template v-if="item.actionDetails?.tier_inventory_percentage">
            {{ percentageFormatter.format(item.actionDetails?.tier_inventory_percentage) }}
          </template>
        </template>

        <template v-if="item.targetType === 'event_tier'">
          <template v-if="item.actionDetails?.tier_price">
            {{ currencyFormatter.format(item.actionDetails?.tier_price) }}
          </template>
          <template v-else>
            ?
          </template>
          up to
          <template v-if="item.actionDetails?.tier_inventory_percentage">
            {{ percentageFormatter.format(item.actionDetails?.tier_inventory_percentage) }}
          </template>
        </template>

      </template>

    </audit-log-table>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import AuditLogTable from '../../AuditLogTable.vue'

// models
import Location from '../../../models/Location'

// misc
import ChannelRegistry from '../../../channels/ChannelRegistry'
import { Temporal } from '@js-temporal/polyfill'
import { required, integer, positive } from '../../../validation'
import type { Can } from '../../../main'
import type { FetchAuditLogsResponseRecord } from '../../../models/AuditLog'
import { percentageFormatter } from '../../../utils/formatters'

interface Props {
  locationID: number
}

const props = defineProps<Props>()

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')
const canEditLocationProducts = $can('canLocationProductsWrite')

const { t } = useI18n()

const title = useTitle()
title.value = 'Rate Management: Log'

/**
 * Filter form
 */

interface FormState {
  targetId: null | number
  targetTypes: string[]
}

const formState = reactive<FormState>({
  targetId: null,
  targetTypes: ['default_rate', 'spot_rate', 'event_tier', 'venue_location_product', 'venue_tier'],
})

const pendingFormState = reactive({
  targetId: formState.targetId,
  targetTypes: formState.targetTypes,
})

const pendingFormRules = {
  targetId: [integer(), positive()],
  targetTypes: [required()],
}

/**
 * Table config
 */

const headers = [
  {key: 'productName', title: t('titles.product'), sortable: false},
  {key: 'targetType', title: t('titles.target_type')},
  {key: 'targetID', title: t('titles.target_id')},
  {key: 'action', title: t('titles.action')},
  {key: 'venueName', title: t('titles.venue'), sortable: false},
  {key: 'eventName', title: t('titles.event'), sortable: false},
  {key: 'startDate', title: t('titles.dates_one_or_more'), sortable: false},
  {key: 'channel', title: t('titles.channel'), sortable: false},
  {key: 'actionDetails', title: t('titles.summary'), sortable: false},
]

const locationName = computed(() => {
  const location = Location.find(props.locationID)
  return location ? location.locationName : '(unknown)'
})

interface TableItem {
  // Each target_type has its own schema for action_details. TableItem normalizes these disparate schemas to a common
  // format, so we can, like, display it in the table.
  locationProductID: null | number
  productName: string
  venueID: null | number
  venueName: null | string
  eventID: null | number
  eventName: null | string
  startDate: null | string
  endDate: null | string
  channel: null | string
  actionDetails: any
}

function transformer(auditLog: FetchAuditLogsResponseRecord): TableItem {

  const actionDetails = auditLog.actionDetails ?? {}

  const item: TableItem = {
    locationProductID: actionDetails?.location_product_id ?? null,
    productName: actionDetails?.product_name ?? '(unknown)',
    venueID: actionDetails?.venue_id ?? null,
    venueName: actionDetails?.venue_name ?? null,
    eventID: actionDetails?.event_id ?? null,
    eventName: actionDetails?.event_name ?? null,
    startDate: actionDetails?.start_date ?? null,
    endDate: actionDetails?.end_date ?? null,
    channel: actionDetails?.channel ?? null,
    actionDetails: actionDetails,
  }

  return item

}

/**
 * Miscellaneous
 */

const currencyFormatter = Intl.NumberFormat('en-US',
  { style: 'currency', currency: 'USD' }
)

</script>
